import type { EventBus } from "../core/EventBus.ts";
import type { Tower } from "../entities/Tower.ts";
import type { Enemy } from "../entities/Enemy.ts";
import type {
  SynergyDataFile,
  SynergyDefinition,
  ActiveSynergy,
  VirusSynergyMods,
} from "../entities/types.ts";
import { detectSynergies } from "./SynergyDetection.ts";
import {
  updateSynergyEffects,
  getStunChanceForTower,
} from "./SynergyEffects.ts";

/**
 * Orchestrator: combines synergy detection and effect application.
 * Public API is unchanged from the monolithic version.
 */
export class SynergySystem {
  private towers: Tower[];
  private synergyData: SynergyDefinition[];
  private bus: EventBus;
  private activeSynergies_: ActiveSynergy[] = [];
  private allowDiagonal_ = false;
  private frequencyMultiplier_ = 1;
  private triComboAutoFire_ = false;
  private effectMultiplier_ = 1;
  private resonanceEnabled_ = false;
  private monocultureEnabled_ = false;

  constructor(towers: Tower[], synergyData: SynergyDataFile, bus: EventBus) {
    this.towers = towers;
    this.synergyData = synergyData.synergies;
    this.bus = bus;
  }

  setTowers(list: Tower[]): void {
    this.towers = list;
  }
  set allowDiagonal(val: boolean) {
    this.allowDiagonal_ = val;
  }
  set frequencyMultiplier(val: number) {
    this.frequencyMultiplier_ = Math.min(val, 4.0); // CR-17: cap
  }
  set triComboAutoFire(val: boolean) {
    this.triComboAutoFire_ = val;
  }

  set effectMultiplier(val: number) {
    this.effectMultiplier_ = Math.min(val, 4.0); // CR-17: cap
  }

  get effectMultiplier(): number {
    return this.effectMultiplier_;
  }

  set resonanceEnabled(val: boolean) {
    this.resonanceEnabled_ = val;
  }

  get resonanceEnabled(): boolean {
    return this.resonanceEnabled_;
  }

  get activeSynergies(): readonly ActiveSynergy[] {
    return this.activeSynergies_;
  }
  get frequencyMult(): number {
    return this.frequencyMultiplier_;
  }
  get hasTriComboAutoFire(): boolean {
    return this.triComboAutoFire_;
  }

  get monocultureEnabled(): boolean {
    return this.monocultureEnabled_;
  }

  set monocultureEnabled(val: boolean) {
    this.monocultureEnabled_ = val;
  }

  reset(): void {
    this.activeSynergies_ = [];
    this.allowDiagonal_ = false;
    this.frequencyMultiplier_ = 1;
    this.triComboAutoFire_ = false;
    this.effectMultiplier_ = 1;
    this.resonanceEnabled_ = false;
    this.monocultureEnabled_ = false;
  }

  recalculate(): void {
    const prev = this.activeSynergies_.length;
    this.activeSynergies_ = detectSynergies({
      towers: this.towers,
      synergyData: this.synergyData,
      allowDiagonal: this.allowDiagonal_,
      resonanceEnabled: this.resonanceEnabled_,
      monoculture: this.monocultureEnabled_,
    });

    if (this.activeSynergies_.length !== prev) {
      this.bus.emit("synergy.updated", { synergies: this.activeSynergies_ });
    }
  }

  update(dt: number, enemies: Enemy[], cascadeDamage = 0): void {
    updateSynergyEffects(
      this.activeSynergies_,
      dt,
      enemies,
      this.frequencyMultiplier_,
      this.triComboAutoFire_,
      this.bus,
      this.effectMultiplier_,
      cascadeDamage,
    );
  }

  getStunChance(tower: Tower): number {
    return getStunChanceForTower(tower, this.activeSynergies_);
  }

  /** Check if Target Lock synergy (signal+lightning) is active. */
  hasAutoTargetMarked(): boolean {
    return this.activeSynergies_.some(
      (s) => s.effect.type === "autoTargetMarked",
    );
  }

  /** CR-H8: Check if Burning Mark synergy (signal+fire) is active and
   *  return tick + duration multipliers for DOT on marked targets.
   *  Returns null when synergy is inactive (caller skips boost). */
  getMarkedDotBoost(): { tickMult: number; durationMult: number } | null {
    for (const s of this.activeSynergies_) {
      if (s.effect.type === "markedDotBoost") {
        return {
          tickMult: s.effect.tickMultiplier ?? 2.0,
          durationMult: s.effect.durationMultiplier ?? 2.0,
        };
      }
    }
    return null;
  }

  /** Check if Broadcast synergy (signal+water) is active and return radius. */
  getAoeMarkRadius(): number {
    for (const s of this.activeSynergies_) {
      if (s.effect.type === "aoeMark") return s.effect.aoeRadius ?? 48;
    }
    return 0;
  }

  /**
   * B2 (S41): combined Virus cross-element synergy modifiers.
   * Returns null when no virus synergy is active so callers can early-out.
   */
  getVirusSynergyMods(): VirusSynergyMods | null {
    let hasAny = false;
    const mods: VirusSynergyMods = {
      fireDot: null,
      spreadChanceBonus: 0,
      spreadRadiusMultiplier: 1,
      maxGenerationsBonus: 0,
      markedIgnoreGenerationCap: false,
      markedSpreadChance: 1,
    };

    for (const s of this.activeSynergies_) {
      switch (s.effect.type) {
        case "dualDot":
          hasAny = true;
          mods.spreadChanceBonus += s.effect.spreadChanceBonus ?? 0;
          if (s.effect.applyFireDot) {
            mods.fireDot = {
              tickDamage: s.effect.fireDotTickDamage ?? 4,
              duration: s.effect.fireDotDuration ?? 2000,
            };
          }
          break;
        case "enhancedSpread":
          hasAny = true;
          mods.spreadChanceBonus += s.effect.spreadChanceBonus ?? 0;
          mods.spreadRadiusMultiplier =
            s.effect.spreadRadiusMultiplier ?? mods.spreadRadiusMultiplier;
          mods.maxGenerationsBonus += s.effect.maxGenerationsBonus ?? 0;
          break;
        case "unlimitedSpreadOnMarked":
          hasAny = true;
          mods.markedIgnoreGenerationCap =
            s.effect.markedIgnoreGenerationCap ?? true;
          mods.markedSpreadChance = s.effect.markedSpreadChance ?? 1;
          break;
      }
    }

    return hasAny ? mods : null;
  }
}
